import {
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import AurStepper from '../../common/aur-stepper-tab/AurStepperTab';

import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import {ReactComponent as BackgroundImage} from '../../../assests/svg/glow-right.svg';
import ExportForms from './ExportForms';
import CreateExportStep2 from './CreateExportStep2';
import {RootState} from '../../../redux/store';
import {useSelector, useDispatch} from 'react-redux';
import {getTagData, getExportList} from '../../../api/EcaManagementService';
import {
  getEcaFields,
  getIndexByClientIdAndProjectId,
  getRelationalGroups,
} from '../../../api/EcaServices';
import {setExportTab1Params} from '../../../redux/slices/addExportSlice';
import {ExportNameData, FieldData, RelationalGrp} from './CreateExportTypes';
import {EciHomePageTabs} from '../../../constants/constants';
import {
  GA_EVENT_NAMES,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

const CreateExportStep1: React.FC = () => {
  const steps = [
    {label: t('wizard.generalInfo')},
    {label: t('wizard.selectFields')},
  ];
  const [activeStep, setActiveStep] = useState(0);
  const {trackGAEvent} = useAnalytics();

  const [selectedTagging, setSelectedTagging] =
    useState<string>('export_for_review');
  const [tagData, setTagData] = useState<ITags[]>([]);
  const [exportNames, setExportNames] = useState<ExportNameData[]>([]);
  const [fieldData, setFieldData] = useState<FieldData[]>([]);
  const [groupIdentifierId, setGroupIdentifierId] = useState<number | null>(
    null
  );

  interface ITags {
    id: string; // Updated id to be a string, as per the API response
    label: string;
    name: string;
    values: any[]; // Nested array of values
  }

  const handleTaggingSelection = (value: string) => {
    setSelectedTagging(value);
  };

  const [loadComponent, setLoadComponent] = useState<boolean>(false);
  const [fieldTempId, setFieldTempId] = useState<number>(1);

  const dispatch = useDispatch();

  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );

  const breadCrumbItems = [
    {
      label: t('eca.ecaHomepage.coreEciHome'),
      path: `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`,
    },
    {
      label: t('ecaHomepageModals.exportModal.goToExportManagement'),
      path: '/eci-home-page/export-management',
    },
    {
      label: t('eca.newExport.createNew'),
    },
  ];

  const radioOptions = [
    {value: 'export_for_review', label: t('eca.newExport.exportReview')},
    {value: 'export_for_analytics', label: t('eca.newExport.exportAnalytic')},
  ];

  const exportFileType = [
    {
      id: '1',
      name: t('eca.newExport.dat'),
    },
    {
      id: '2',
      name: t('eca.newExport.csv'),
    },
  ];

  useEffect(() => {
    getTagDropDown();
    getExportDataList();
    fetchAvailableField();
    fetchRelationGroups();
    dispatch(setExportTab1Params({}));
    setLoadComponent(true);
    getIndexList(clientId!, projectId!);
  }, []);

  const getTagDropDown = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await getTagData(params);
      if (results.status == 200 || results.status == 201) {
        const tags = results?.data?.data;
        setTagData(tags);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchAvailableField = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}&skip=0&top=2000`;
    try {
      const results = await getEcaFields(params);
      const dataField = results?.data?.data;
      setFieldData(dataField);
    } catch (e) {
      console.log(e);
    }
  };

  const getExportDataList = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await getExportList(params);
      const exportData = results?.data?.data;
      setExportNames(exportData);
    } catch (e) {
      console.log(e);
    }
  };

  const getIndexList = async (clientId: string, projectId: string) => {
    try {
      const response = await getIndexByClientIdAndProjectId(
        clientId,
        projectId
      );
      if (response?.status == 200) {
        if (response?.data?.data?.length) {
          const standeredIndex = response.data.data.find(
            (i: any) => i.type.toLowerCase() == 'STANDARD'.toLowerCase()
          );
          setFieldTempId(standeredIndex.fieldTemplateId);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRelationGroups = async () => {
    try {
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await getRelationalGroups(params);
      if (result?.status == 200) {
        if (result?.data?.data?.length) {
          const match = result?.data?.data?.find(
            (item: RelationalGrp) => item.name === 'GroupIdentifier'
          );
          if (match) {
            setGroupIdentifierId(match.id);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (activeStep === 1) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.export.selectFields'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
  }, [activeStep]);

  return (
    <div
      className={`add-export-header ${
        activeStep === 2
          ? 'aur-add-edit-user create-custom-index'
          : 'aur-custom-index'
      }`}
    >
      <div
        className={
          activeStep === 2 ? 'header-background' : 'header-background-custom'
        }
      >
        <div className="header">
          <Box>
            <AurBreadcrumb items={breadCrumbItems} />
          </Box>
          <Typography
            className="typo-text headline1_size custom-index-heading"
            component="h1"
          >
            {t('eca.newExport.createNew')}
          </Typography>
          <AurStepper
            activeStep={activeStep}
            steps={steps}
            setActiveStep={setActiveStep}
          />
        </div>
        <BackgroundImage className="background-image" aria-hidden="true" />
      </div>

      <div
        className={`${activeStep == 2 ? 'content analyzer-content' : 'content content-backgroundcolor'}`}
      >
        {activeStep == 0 ? (
          <Box className="box-1-container">
            <Typography className="typo-text headline3_size heading-1">
              {t('eca.newExport.startBasics')}
            </Typography>

            <div style={{paddingTop: '20px'}}>
              <Typography className="typo-text subhead4_size">
                {t('eca.newExport.whatType')}
              </Typography>

              <RadioGroup
                row
                value={selectedTagging}
                onChange={(event) => handleTaggingSelection(event.target.value)}
                aria-label={t('common.typeOfExport')}
              >
                {radioOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': {
                            color: '#fff', // Checkmark color
                            '& .MuiSvgIcon-root': {
                              backgroundColor: '#5265FF', // Blue background for outer circle when checked
                              borderColor: '#5265FF',
                              borderRadius: '50%', // Blue border for outer circle when checked
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        className="typo-text subhead3_size"
                        sx={{color: '#F2F4F7'}}
                      >
                        {option.label}
                      </Typography>
                    }
                    sx={{
                      backgroundColor: '#121A25',
                      width: '332px',
                      margin: '12px 8px 20px 5px',
                      padding: '5px',
                      ...(selectedTagging === option.value && {
                        borderBottom: '1px solid #2D3D55',
                      }),
                    }}
                  />
                ))}
              </RadioGroup>
            </div>

            <div>
              {loadComponent && (
                <ExportForms
                  radioBtnType={selectedTagging}
                  setActiveStep={setActiveStep}
                  tagsData={tagData}
                  exportNamesList={exportNames}
                  activeStep={activeStep}
                  fieldTempId={fieldTempId}
                  exportFileType={exportFileType}
                  groupIdentifierId={groupIdentifierId}
                />
              )}
            </div>
          </Box>
        ) : null}
        {activeStep == 1 ? (
          <Box className="box-2-container">
            <>
              <Typography className="typo-text headline3_size mist-color-100 title-1">
                {t('eca.newExport.addFieldExport')}
              </Typography>
              <Typography className="body2_size mist-color-700 title-2">
                {t('eca.newExport.chooseFromField')}
              </Typography>
            </>
            {loadComponent && (
              <CreateExportStep2
                fieldData={fieldData}
                radioBtnType={selectedTagging}
              />
            )}
          </Box>
        ) : null}
      </div>
    </div>
  );
};

export default CreateExportStep1;
